import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    const isAuthEndpoint = err.config?.url?.includes('/auth/login') || err.config?.url?.includes('/auth/setup');
    if (!isAuthEndpoint && (err.response?.status === 401 || err.response?.status === 403)) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  checkSetup: () => api.get('/auth/check-setup'),
  setup: (data) => api.post('/auth/setup', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
};

export const vertraegeApi = {
  list: () => api.get('/vertraege'),
  uebersicht: () => api.get('/vertraege/uebersicht'),
  get: (id) => api.get(`/vertraege/${id}`),
  akte: (id) => api.get(`/vertraege/${id}/akte`),
  create: (data) => api.post('/vertraege', data),
  update: (id, data) => api.put(`/vertraege/${id}`, data),
  delete: (id) => api.delete(`/vertraege/${id}`),
  berechne: (data) => api.post('/vertraege/berechne', data),
  timeline: (id) => api.get(`/vertraege/${id}/timeline`),
  simulieren: (id, data) => api.post(`/vertraege/${id}/simulieren`, data),
  prognose: (id, zyklen) => api.get(`/vertraege/${id}/prognose`, { params: { zyklen: zyklen || 3 } }),
  historie: (id) => api.get(`/vertraege/${id}/historie`),
  kalenderIcs: (id) => `/api/vertraege/${id}/kalender.ics`,
};

export const statistikenApi = {
  get: () => api.get('/statistiken'),
};

export const dashboardApi = {
  cockpit: () => api.get('/dashboard/cockpit'),
  fristen: () => api.get('/dashboard/fristen'),
  dokumente: () => api.get('/dashboard/dokumente'),
  kuendigungen: () => api.get('/dashboard/kuendigungen'),
};

export const kalenderApi = {
  ereignisse: (params) => api.get('/kalender/ereignisse', { params }),
  exportIcsUrl: () => {
    const token = localStorage.getItem('token');
    return `/api/kalender/export.ics?token=${encodeURIComponent(token || '')}`;
  },
};

export const assistantApi = {
  chat: (data) => api.post('/assistant/chat', data),
  providers: () => api.get('/assistant/providers'),
  getSettings: () => api.get('/assistant/settings'),
  updateSettings: (data) => api.put('/assistant/settings', data),
  conversations: () => api.get('/assistant/conversations'),
  conversation: (id) => api.get(`/assistant/conversations/${id}`),
};

export const kuendigungenApi = {
  // Legacy-Endpoints (Release 2.x)
  list: () => api.get('/kuendigungen'),
  vorschau: (vertragId, vorlage = 'standard') => api.post('/kuendigungen/vorschau', { vertrag_id: vertragId, vorlage }),
  // Release 2.3 – Vorgänge
  vorgaenge: {
    list: (params) => api.get('/kuendigungen/vorgaenge', { params }),
    create: (data) => api.post('/kuendigungen/vorgaenge', data),
    stats: () => api.get('/kuendigungen/vorgaenge/stats'),
    get: (id) => api.get(`/kuendigungen/vorgaenge/${id}`),
    update: (id, data) => api.put(`/kuendigungen/vorgaenge/${id}`, data),
    delete: (id) => api.delete(`/kuendigungen/vorgaenge/${id}`),
    setStatus: (id, status, notiz) => api.post(`/kuendigungen/vorgaenge/${id}/status`, { status, notiz }),
    // Schreiben
    generiereSchreiben: (id, vorlage_id, plaetze) => api.post(`/kuendigungen/vorgaenge/${id}/schreiben`, { vorlage_id, plaetze }),
    getSchreiben: (id) => api.get(`/kuendigungen/vorgaenge/${id}/schreiben`),
    // Exporte
    exportPdf: (id) => api.get(`/kuendigungen/vorgaenge/${id}/export/pdf`, { responseType: 'blob' }),
    exportDocx: (id) => api.get(`/kuendigungen/vorgaenge/${id}/export/docx`, { responseType: 'blob' }),
    exportEml: (id) => api.get(`/kuendigungen/vorgaenge/${id}/export/eml`, { responseType: 'blob' }),
    // Nachweise
    getNachweise: (id) => api.get(`/kuendigungen/vorgaenge/${id}/nachweise`),
    addNachweis: (id, formData) => api.post(`/kuendigungen/vorgaenge/${id}/nachweise`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    // Kommunikation
    getKommunikation: (id) => api.get(`/kuendigungen/vorgaenge/${id}/kommunikation`),
    addKommunikation: (id, data) => api.post(`/kuendigungen/vorgaenge/${id}/kommunikation`, data),
    // Wiedervorlagen
    getWiedervorlagen: (id) => api.get(`/kuendigungen/vorgaenge/${id}/wiedervorlagen`),
    addWiedervorlage: (id, data) => api.post(`/kuendigungen/vorgaenge/${id}/wiedervorlagen`, data),
  },
  // Release 2.3 – Schreiben (standalone)
  schreiben: {
    update: (sid, betreff, text) => api.put(`/kuendigungen/schreiben/${sid}`, { betreff, text }),
    freigeben: (sid) => api.post(`/kuendigungen/schreiben/${sid}/freigeben`),
    regenerieren: (sid, vorlage_id, plaetze) => api.post(`/kuendigungen/schreiben/${sid}/regenerieren`, { vorlage_id, plaetze }),
  },
  // Release 2.3 – Nachweise (standalone)
  nachweise: {
    delete: (nid, kuendigung_id) => api.delete(`/kuendigungen/nachweise/${nid}`, { params: { kuendigung_id } }),
  },
  // Release 2.3 – Kommunikation (standalone)
  kommunikation: {
    updateStatus: (kid, status) => api.put(`/kuendigungen/kommunikation/${kid}/status`, { status }),
  },
  // Release 2.3 – Wiedervorlagen (standalone)
  wiedervorlagen: {
    update: (wid, data) => api.put(`/kuendigungen/wiedervorlagen/${wid}`, data),
    delete: (wid) => api.delete(`/kuendigungen/wiedervorlagen/${wid}`),
  },
  // Release 2.3 – Vorlagen
  vorlagen: {
    list: () => api.get('/kuendigungs-vorlagen'),
    create: (data) => api.post('/kuendigungs-vorlagen', data),
    update: (id, data) => api.put(`/kuendigungs-vorlagen/${id}`, data),
    delete: (id) => api.delete(`/kuendigungs-vorlagen/${id}`),
  },
};

export const kiAnalyseApi = {
  zusammenfassung: (id, refresh) => api.get(`/ki/vertrag/${id}/zusammenfassung`, { params: refresh ? { refresh: '1' } : {} }),
  klauseln: (id, refresh) => api.get(`/ki/vertrag/${id}/klauseln`, { params: refresh ? { refresh: '1' } : {} }),
  preisaenderungen: (id) => api.get(`/ki/vertrag/${id}/preisaenderungen`),
  sonderkuendigung: (id) => api.get(`/ki/vertrag/${id}/sonderkuendigung`),
  vergleich: (id1, id2) => api.post('/ki/vertraege/vergleich', { vertrag_id_1: id1, vertrag_id_2: id2 }),
};

export const searchApi = {
  global: (q, params = {}) => api.get('/search', { params: { q, ...params } }),
  suggestions: (q) => api.get('/search/suggestions', { params: { q } }),
  history: () => api.get('/search/history'),
};

export const dokumenteApi = {
  list: (params) => api.get('/dokumente', { params }),
  detail: (id) => api.get(`/dokumente/${id}`),
  ocrNeustart: (id) => api.post(`/dokumente/${id}/ocr-neustart`),
  analyse: (id) => api.post(`/dokumente/${id}/analyse`),
  analysieren: (id) => api.post(`/dokumente/${id}/analysieren`),
  kiNeustart: (id) => api.post(`/dokumente/${id}/ki-neustart`),
  status: (id) => api.get(`/dokumente/${id}/status`),
  felder: (id) => api.get(`/dokumente/${id}/felder`),
  feldBestaetigen: (id, feldId, aktion) => api.post(`/dokumente/${id}/felder/${feldId}/bestaetigen`, { aktion }),
  felderUebernehmen: (id, data) => api.post(`/dokumente/${id}/felder-uebernehmen`, data),
  zuordnen: (id, vertragId) => api.post(`/dokumente/${id}/zuordnen`, { vertrag_id: vertragId }),
  neuerVertrag: (id, data) => api.post(`/dokumente/${id}/neuer-vertrag`, data),
  vorschlaege: (id) => api.get(`/dokumente/${id}/vorschlaege`),
  analyseDetail: (id) => api.get(`/dokumente/${id}/analyse`),
  uploadFrei: (formData, onProgress) =>
    api.post('/dokumente/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    }),
  previewUrl: (url) => {
    const token = localStorage.getItem('token');
    const p = url || '';
    const separator = p.includes('?') ? '&' : '?';
    return `${p}${separator}token=${encodeURIComponent(token || '')}`;
  },
  upload: (vertragId, formData, onProgress) =>
    api.post(`/vertraege/${vertragId}/dokumente`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    }),
  delete: (id) => api.delete(`/dokumente/${id}`),
};

export const sucheApi = {
  volltext: (q, params) => api.get('/suche/volltext', { params: { q, ...params } }),
};

export const kategorienApi = {
  list: () => api.get('/kategorien').then(r => r.data),
  create: (data) => api.post('/kategorien', data).then(r => r.data),
  update: (id, data) => api.put(`/kategorien/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/kategorien/${id}`).then(r => r.data),
};

export default api;

export const preishistorieApi = {
  get: (vertragId) => api.get(`/vertraege/${vertragId}/preishistorie`),
  add: (vertragId, data) => api.post(`/vertraege/${vertragId}/preishistorie`, data),
  alle: () => api.get('/preishistorie/alle'),
};

export const kiApi = {
  analyse: (text) => api.post('/ki/analyse', { text }),
  analyseDokument: (dokumentId) => api.post(`/ki/analyse-dokument/${dokumentId}`),
};

export const ausgabenApi = {
  list: (params) => api.get('/ausgaben', { params }),
  get: (id) => api.get(`/ausgaben/${id}`),
  create: (formData) => api.post('/ausgaben', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, formData) => api.put(`/ausgaben/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/ausgaben/${id}`),
  auswertung: (jahr) => api.get('/ausgaben-auswertung', { params: { jahr } }),
  exportUrl: (params) => {
    const token = localStorage.getItem('token');
    const p = new URLSearchParams({ ...params, token });
    return (process.env.REACT_APP_API_URL || '') + '/api/ausgaben-export?' + p.toString();
  },
};

export const zahlungenApi = {
  list: (params) => api.get('/zahlungen', { params }),
  get: (id) => api.get(`/zahlungen/${id}`),
  create: (data) => api.post('/zahlungen', data),
  update: (id, data) => api.put(`/zahlungen/${id}`, data),
  delete: (id) => api.delete(`/zahlungen/${id}`),
  auswertung: () => api.get('/zahlungen-auswertung'),
};

export const budgetApi = {
  list: () => api.get('/budgets'),
  create: (data) => api.post('/budgets', data),
  update: (id, data) => api.put(`/budgets/${id}`, data),
  delete: (id) => api.delete(`/budgets/${id}`),
  auswertung: () => api.get('/budget-auswertung'),
};

export const berichtApi = {
  senden: () => api.post('/finanzbericht/senden'),
};

// Release 2.4 – Masterblock 1
export const aufgabenApi = {
  list: (params) => api.get('/aufgaben', { params }),
  get: (id) => api.get(`/aufgaben/${id}`),
  create: (data) => api.post('/aufgaben', data),
  update: (id, data) => api.put(`/aufgaben/${id}`, data),
  statistik: () => api.get('/aufgaben/statistik'),
  erledigen: (id, notiz) => api.post(`/aufgaben/${id}/erledigen`, { notiz }),
  ignorieren: (id, notiz) => api.post(`/aufgaben/${id}/ignorieren`, { notiz }),
  wiedereroeffnen: (id) => api.post(`/aufgaben/${id}/wiedereroeffnen`),
  verschieben: (id, faellig_am) => api.post(`/aufgaben/${id}/verschieben`, { faellig_am }),
  historie: (id) => api.get(`/aufgaben/${id}/historie`),
  kommentieren: (id, kommentar) => api.post(`/aufgaben/${id}/kommentare`, { kommentar }),
  bulk: (ids, action, data) => api.post('/aufgaben/bulk', { ids, action, data }),
};

export const gesundheitApi = {
  uebersicht: () => api.get('/gesundheit/uebersicht'),
  vertrag: (id) => api.get(`/vertraege/${id}/gesundheit`),
  berechnen: (id) => api.post(`/vertraege/${id}/gesundheit/berechnen`),
  historie: (id) => api.get(`/vertraege/${id}/gesundheit/historie`),
};

export const empfehlungenApi = {
  list: (params) => api.get('/empfehlungen', { params }),
  statistik: () => api.get('/empfehlungen/statistik'),
  annehmen: (id) => api.post(`/empfehlungen/${id}/annehmen`),
  ablehnen: (id) => api.post(`/empfehlungen/${id}/ablehnen`),
  abgeschlossen: (id) => api.post(`/empfehlungen/${id}/abgeschlossen`),
};

export const regelnApi = {
  list: () => api.get('/regeln'),
  get: (id) => api.get(`/regeln/${id}`),
  create: (data) => api.post('/regeln', data),
  update: (id, data) => api.put(`/regeln/${id}`, data),
  aktivieren: (id) => api.post(`/regeln/${id}/aktivieren`),
  deaktivieren: (id) => api.post(`/regeln/${id}/deaktivieren`),
  ausfuehrungen: (id) => api.get(`/regeln/${id}/ausfuehrungen`),
};

export const workflowsApi = {
  definitionen: () => api.get('/workflows/definitionen'),
  runs: (params) => api.get('/workflows/runs', { params }),
  starten: (workflow_key, kontext, dedup_key) => api.post('/workflows/starten', { workflow_key, kontext, dedup_key }),
  getRun: (id) => api.get(`/workflows/runs/${id}`),
  schritte: (id) => api.get(`/workflows/runs/${id}/schritte`),
  pausieren: (id) => api.post(`/workflows/runs/${id}/pausieren`),
  fortsetzen: (id) => api.post(`/workflows/runs/${id}/fortsetzen`),
  abbrechen: (id, grund) => api.post(`/workflows/runs/${id}/abbrechen`, { grund }),
};

export const ereignisseApi = {
  list: (params) => api.get('/ereignisse', { params }),
  get: (id) => api.get(`/ereignisse/${id}`),
  wiederholen: (id) => api.post(`/ereignisse/${id}/wiederholen`),
  ignorieren: (id) => api.post(`/ereignisse/${id}/ignorieren`),
};

export const smartDashboardApi = {
  get: () => api.get('/dashboard/smart'),
  automatisierung: () => api.get('/automatisierung/status'),
};

export const haushaltApi = {
  kategorien: { list: () => api.get('/hh/kategorien'), create: (d) => api.post('/hh/kategorien', d), delete: (id) => api.delete(`/hh/kategorien/${id}`) },
  konten: { list: () => api.get('/hh/konten'), create: (d) => api.post('/hh/konten', d) },
  buchungen: { list: (p) => api.get('/hh/buchungen', { params: p }), create: (d) => api.post('/hh/buchungen', d), update: (id, d) => api.put(`/hh/buchungen/${id}`, d), delete: (id) => api.delete(`/hh/buchungen/${id}`) },
  auswertung: (jahr) => api.get('/hh/auswertung', { params: { jahr } }),
  csvImport: (formData) => api.post('/hh/csv-import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// ─── Release 2.4 MB2 ──────────────────────────────────────────────────────────
export const dokumenteV2Api = {
  list: (params) => api.get('/dokumente-v2', { params }),
  get: (id) => api.get(`/dokumente-v2/${id}`),
  verarbeitenV2: (id, data) => api.post(`/dokumente/${id}/verarbeiten-v2`, data || {}),
  // Klauseln
  klauselAnalysieren: (id) => api.post(`/dokumente/${id}/klauseln/analysieren`),
  klauseln: (id, params) => api.get(`/dokumente/${id}/klauseln`, { params }),
  klauselBestaetigen: (id) => api.put(`/klauseln/${id}/bestaetigen`),
  klauselAblehnen: (id, grund) => api.put(`/klauseln/${id}/ablehnen`, { grund }),
  alleKlauseln: (params) => api.get('/klauseln', { params }),
  // Versionierung
  versionen: (id) => api.get(`/dokumente/${id}/versionen`),
  vergleich: (a, b) => api.post('/dokumente/vergleich', { dokument_id_a: a, dokument_id_b: b }),
  versionsgruppen: (params) => api.get('/dokument-versionsgruppen', { params }),
  versionsgruppeGet: (id) => api.get(`/dokument-versionsgruppen/${id}`),
  versionsgruppeCreate: (data) => api.post('/dokument-versionsgruppen', data),
  // Qualität
  qualitaetBerechnen: (id) => api.post(`/dokumente/${id}/qualitaet/berechnen`),
  qualitaetStatistik: () => api.get('/dokumente/qualitaet/statistik'),
  // Suche
  suche: (q, params) => api.get('/dokumente/suche', { params: { q, ...params } }),
  // Felder (Prüf-Center)
  felder: (id) => api.get(`/dokumente/${id}/felder`),
  feldBestaetigen: (dokId, feldId) => api.put(`/dokumente/${dokId}/felder/${feldId}/bestaetigen`),
  feldAblehnen: (dokId, feldId, grund) => api.put(`/dokumente/${dokId}/felder/${feldId}/ablehnen`, { grund }),
  feldUebernehmen: (dokId, feldId) => api.post(`/dokumente/${dokId}/felder/${feldId}/uebernehmen`),
  // Seiten
  seiten: (id) => api.get(`/dokumente/${id}/seiten`),
  // Ereignisse/Aufgaben
  ereignisse: (id) => api.get(`/dokumente/${id}/ereignisse`),
  aufgaben: (id) => api.get(`/dokumente/${id}/aufgaben`),
  // Dashboard
  dashboard: () => api.get('/dashboard/dokumentenintelligenz'),
};

export const wissenApi = {
  list: (params) => api.get('/wissen', { params }),
  add: (data) => api.post('/wissen', data),
  verifizieren: (id) => api.put(`/wissen/${id}/verifizieren`),
  delete: (id) => api.delete(`/wissen/${id}`),
  indexieren: (dokId) => api.post(`/dokumente/${dokId}/wissen/indexieren`),
  suche: (q, params) => api.get('/wissen/suche', { params: { q, ...params } }),
  fragen: (frage, vertrag_id) => api.post('/wissen/fragen', { frage, vertrag_id }),
  fragenliste: (params) => api.get('/wissen/fragen', { params }),
  klauselSuche: (q, params) => api.get('/klauseln/suche', { params: { q, ...params } }),
  felderSuche: (q, params) => api.get('/felder/suche', { params: { q, ...params } }),
};

// ─── Release 2.4 MB3 ──────────────────────────────────────────────────────────
export const anbieterApi = {
  list: (params) => api.get('/anbieter', { params }).then(r => r.data),
  get: (id) => api.get(`/anbieter/${id}`).then(r => r.data),
  create: (data) => api.post('/anbieter', data).then(r => r.data),
  update: (id, data) => api.put(`/anbieter/${id}`, data).then(r => r.data),
  archivieren: (id) => api.post(`/anbieter/${id}/archivieren`).then(r => r.data),
  merge: (quell_id, ziel_id) => api.post('/anbieter/merge', { quell_id, ziel_id }).then(r => r.data),
  migration: () => api.post('/anbieter/migration').then(r => r.data),
  kosten: (id) => api.get(`/anbieter/${id}/kosten`).then(r => r.data),
  addAdresse: (id, data) => api.post(`/anbieter/${id}/adressen`, data).then(r => r.data),
  addKanal: (id, data) => api.post(`/anbieter/${id}/kanaele`, data).then(r => r.data),
  deleteKanal: (kanId) => api.delete(`/anbieter/kanaele/${kanId}`).then(r => r.data),
};

export const kontakteApi = {
  list: (params) => api.get('/kontakte', { params }).then(r => r.data),
  get: (id) => api.get(`/kontakte/${id}`).then(r => r.data),
  create: (data) => api.post('/kontakte', data).then(r => r.data),
  update: (id, data) => api.put(`/kontakte/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/kontakte/${id}`).then(r => r.data),
  setPrimaer: (id) => api.post(`/kontakte/${id}/primaer`).then(r => r.data),
  merge: (quell_id, ziel_id) => api.post('/kontakte/merge', { quell_id, ziel_id }).then(r => r.data),
  historie: (id) => api.get(`/kontakte/${id}/historie`).then(r => r.data),
};

export const kommunikationApi = {
  list: (params) => api.get('/kommunikation', { params }).then(r => r.data),
  get: (id) => api.get(`/kommunikation/${id}`).then(r => r.data),
  create: (data) => api.post('/kommunikation', data).then(r => r.data),
  update: (id, data) => api.put(`/kommunikation/${id}`, data).then(r => r.data),
  updateStatus: (id, status) => api.post(`/kommunikation/${id}/status`, { status }).then(r => r.data),
  addNachricht: (id, data) => api.post(`/kommunikation/${id}/nachrichten`, data).then(r => r.data),
  createEmailEntwurf: (data) => api.post('/kommunikation/email-entwurf', data).then(r => r.data),
  createTelefonnotiz: (data) => api.post('/kommunikation/telefonnotiz', data).then(r => r.data),
  threads: (params) => api.get('/kommunikation/threads', { params }).then(r => r.data),
  threadDetail: (id) => api.get(`/kommunikation/threads/${id}`).then(r => r.data),
};

export const kostenApi = {
  list: (vertragId, params) => api.get(`/vertraege/${vertragId}/kosten`, { params }).then(r => r.data),
  get: (id) => api.get(`/kosten/${id}`).then(r => r.data),
  create: (vertragId, data) => api.post(`/vertraege/${vertragId}/kosten`, data).then(r => r.data),
  update: (id, data) => api.put(`/kosten/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/kosten/${id}`).then(r => r.data),
  historie: (id) => api.get(`/kosten/${id}/historie`).then(r => r.data),
  uebersicht: () => api.get('/kosten/uebersicht').then(r => r.data),
  prognose: (monate) => api.get('/kosten/prognose', { params: { monate } }).then(r => r.data),
  einsparungen: () => api.get('/kosten/einsparungen').then(r => r.data),
};

export const importApi = {
  upload: (datei, typ, format) => {
    const fd = new FormData();
    fd.append('datei', datei);
    fd.append('typ', typ);
    fd.append('format', format);
    return api.post('/import/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  testlauf: (jobId, mapping) => api.post(`/import/${jobId}/testlauf`, { mapping }).then(r => r.data),
  execute: (jobId, mapping) => api.post(`/import/${jobId}/execute`, { mapping }).then(r => r.data),
  rollback: (jobId) => api.post(`/import/${jobId}/rollback`).then(r => r.data),
  status: (jobId) => api.get(`/import/${jobId}/status`).then(r => r.data),
  jobs: () => api.get('/import/jobs').then(r => r.data),
};

export const exportApi = {
  create: (typ, format, filter) => api.post('/export', { export_typ: typ, format, filter }).then(r => r.data),
  list: () => api.get('/export/jobs').then(r => r.data),
  status: (jobId) => api.get(`/export/${jobId}/status`).then(r => r.data),
  downloadUrl: (jobId) => {
    const token = localStorage.getItem('token');
    return `/api/export/${jobId}/download?token=${encodeURIComponent(token || '')}`;
  },
};

export const uiSettingsApi = {
  get: () => api.get('/ui-einstellungen').then(r => r.data),
  update: (data) => api.put('/ui-einstellungen', data).then(r => r.data),
  filters: (params) => api.get('/filter', { params }).then(r => r.data),
  createFilter: (data) => api.post('/filter', data).then(r => r.data),
  updateFilter: (id, data) => api.put(`/filter/${id}`, data).then(r => r.data),
  deleteFilter: (id) => api.delete(`/filter/${id}`).then(r => r.data),
};

// MB4
export const profilApi = {
  me: () => api.get('/auth/me').then(r => r.data),
  update: (data) => api.put('/auth/me', data).then(r => r.data),
  changePassword: (data) => api.put('/auth/passwort', data).then(r => r.data),
};

export const reportsApi = {
  uebersicht: () => api.get('/reports/uebersicht').then(r => r.data),
  vertraege: (params) => api.get('/reports/vertraege', { params }).then(r => r.data),
  kosten: (params) => api.get('/reports/kosten', { params }).then(r => r.data),
  finanzen: (params) => api.get('/reports/finanzen', { params }).then(r => r.data),
};

// EPIC 7 – Vertragsoptimierer
export const optimierungApi = {
  analyse: () => api.get('/optimierung').then(r => r.data),
  vertrag: (id) => api.get(`/optimierung/${id}`).then(r => r.data),
};

// EPIC 8 – Fristen-, Verpflichtungs- & Risikoradar
export const radarApi = {
  analyse: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/radar${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
};

// EPIC 9 – KI-Vertragsvergleich, Dokument-Diff & Change Intelligence
export const vergleichApi = {
  vertraege: (leftContractId, rightContractId) =>
    api.post('/vergleich/vertraege', { leftContractId, rightContractId }).then(r => r.data),
  dokumente: (leftDocumentId, rightDocumentId) =>
    api.post('/vergleich/dokumente', { leftDocumentId, rightDocumentId }).then(r => r.data),
};

// EPIC 10 – Smart Inbox & Auto Intake
export const posteingangApi = {
  list: (params) => api.get('/posteingang', { params }).then(r => r.data),
  summary: () => api.get('/posteingang/summary').then(r => r.data),
  get: (id) => api.get(`/posteingang/${id}`).then(r => r.data),
  upload: (formData) => api.post('/posteingang/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(r => r.data),
  zuordnen: (id, vertrag_id, felder_uebernehmen = [], felder_aendern = []) =>
    api.post(`/posteingang/${id}/zuordnen`, { vertrag_id, felder_uebernehmen, felder_aendern }).then(r => r.data),
  feldvergleich: (id, vertragId) => api.get(`/posteingang/${id}/feldvergleich/${vertragId}`).then(r => r.data),
  neuerVertrag: (id, data) => api.post(`/posteingang/${id}/neuer-vertrag`, data).then(r => r.data),
  retry: (id) => api.post(`/posteingang/${id}/retry`).then(r => r.data),
  ignorieren: (id) => api.post(`/posteingang/${id}/ignorieren`).then(r => r.data),
  reanalysieren: (id) => api.post(`/posteingang/${id}/reanalysieren`).then(r => r.data),
};

// EPIC 11 – Action Center & Activity Intelligence
export const actionsApi = {
  list: (params) => api.get('/actions', { params }).then(r => r.data),
  summary: () => api.get('/actions/summary').then(r => r.data),
  get: (id) => api.get(`/actions/${id}`).then(r => r.data),
  create: (data) => api.post('/actions', data).then(r => r.data),
  update: (id, data) => api.put(`/actions/${id}`, data).then(r => r.data),
  complete: (id) => api.post(`/actions/${id}/complete`).then(r => r.data),
  reopen: (id) => api.post(`/actions/${id}/reopen`).then(r => r.data),
  snooze: (id, snoozed_until) => api.post(`/actions/${id}/snooze`, { snoozed_until }).then(r => r.data),
  dismiss: (id) => api.post(`/actions/${id}/dismiss`).then(r => r.data),
  delete: (id) => api.delete(`/actions/${id}`).then(r => r.data),
  sync: () => api.post('/actions/sync').then(r => r.data),
};

export const activityApi = {
  since: (since) => api.get('/activity/since', since ? { params: { since } } : {}).then(r => r.data),
  seen: () => api.post('/activity/seen').then(r => r.data),
};

// EPIC 12 – Financial Impact & What-If Simulator
export const financialImpactApi = {
  baseline: () => api.get('/financial-impact/baseline').then(r => r.data),
  scenario: (actions) => api.post('/financial-impact/scenario', { actions }).then(r => r.data),
  targetSaving: (targetMonthlySaving) => api.post('/financial-impact/target-saving', { targetMonthlySaving }).then(r => r.data),
};

// EPIC 13 – Notification, Reminder & Calendar Intelligence
export const notificationsApi = {
  list: (params = {}) => api.get('/notifications', { params }).then(r => r.data),
  summary: () => api.get('/notifications/summary').then(r => r.data),
  sync: () => api.post('/notifications/sync').then(r => r.data),
  get: (id) => api.get(`/notifications/${id}`).then(r => r.data),
  read: (id) => api.post(`/notifications/${id}/read`).then(r => r.data),
  readAll: () => api.post('/notifications/read-all').then(r => r.data),
  dismiss: (id) => api.post(`/notifications/${id}/dismiss`).then(r => r.data),
  snooze: (id, snoozeUntil) => api.post(`/notifications/${id}/snooze`, { snoozeUntil }).then(r => r.data),
  digest: (period = 'DAILY') => api.get('/notifications/digest', { params: { period } }).then(r => r.data),
  createManual: (data) => api.post('/notifications/manual', data).then(r => r.data),
  getSettings: () => api.get('/notification-settings').then(r => r.data),
  updateSettings: (data) => api.put('/notification-settings', data).then(r => r.data),
};

export const calendarApi = {
  events: (params = {}) => api.get('/kalender/ereignisse', { params }).then(r => r.data),
  exportIcsUrl: () => '/api/kalender/export.ics',
};

// EPIC 14 – Bank Statement Contract Discovery
export const bankStatementsApi = {
  list: () => api.get('/bank-statements').then(r => r.data),
  get: (id) => api.get(`/bank-statements/${id}`).then(r => r.data),
  upload: (formData) => api.post('/bank-statements/upload', formData).then(r => r.data),
  retry: (id) => api.post(`/bank-statements/${id}/retry`).then(r => r.data),
  transactions: (id, params = {}) => api.get(`/bank-statements/${id}/transactions`, { params }).then(r => r.data),
  summary: () => api.get('/bank-statements-summary').then(r => r.data),
  contractTransactions: (vertragId, params = {}) => api.get(`/vertraege/${vertragId}/banktransaktionen`, { params }).then(r => r.data),
};

export const bankCandidatesApi = {
  list: (params = {}) => api.get('/bank-contract-candidates', { params }).then(r => r.data),
  get: (id) => api.get(`/bank-contract-candidates/${id}`).then(r => r.data),
  match: (id, contract_id) => api.post(`/bank-contract-candidates/${id}/match`, { contract_id }).then(r => r.data),
  ignore: (id) => api.post(`/bank-contract-candidates/${id}/ignore`).then(r => r.data),
  createContract: (id) => api.post(`/bank-contract-candidates/${id}/create-contract`).then(r => r.data),
  applyFields: (id, fields) => api.post(`/bank-contract-candidates/${id}/apply-fields`, { fields }).then(r => r.data),
};

export const paperlessApi = {
  status: () => api.get('/paperless/status').then(r => r.data),
  configure: (data) => api.put('/paperless/connector', data).then(r => r.data),
  scan: () => api.post('/paperless/scan').then(r => r.data),
  results: (params = {}) => api.get('/paperless/results', { params }).then(r => r.data),
  result: (id) => api.get(`/paperless/results/${id}`).then(r => r.data),
  applyAction: (id, action, data = {}) => api.post(`/paperless/results/${id}/apply`, { action, ...data }).then(r => r.data),
};

export const crossSourceApi = {
  run: () => api.post('/cross-source/run').then(r => r.data),
  summary: () => api.get('/cross-source/summary').then(r => r.data),
  links: (params = {}) => api.get('/cross-source/links', { params }).then(r => r.data),
  link: (id) => api.get(`/cross-source/links/${id}`).then(r => r.data),
  dismiss: (id) => api.post(`/cross-source/links/${id}/dismiss`).then(r => r.data),
};
